'use client'

import InstantSell from '@/components/offers/instant-sell'
import MarketTile from '@/components/offers/market-tile'
import React, { FC, ReactElement, useMemo, useCallback, useEffect, useState } from 'react'
import styles from './RemoteCollectionFeed.module.css'
import { API_URL, TEXT_COLLECTION_SLUGS } from '@/utils/constants'

import Loading from '@/components/circle-loading'
import InfiniteScroll from 'react-infinite-scroll-component'
import InscriptionCardWithPreview from '@/components/inscription-card-with-preview'
import { Bitmap3dToggle } from '@/components/bitmap-art/bitmap-3d'
import { BitmapLiveProvider } from '@/components/bitmap-art/live-context'
import ItemGrid from '@/components/item-grid'
import useSWRInfinite from 'swr/infinite'
import useSWR from 'swr'
import { Fetcher } from 'swr'
import InscriptionActions from '@/components/inscription-actions'
import EmptyState from '@/components/empty-state'
import { Collection } from '@/utils/types/collections'
import { Inscription } from '@/utils/types/inscription'
import TraitFilters from '@/components/trait-filters'
import useDebounce from '@/utils/hooks/useDebounce'
import CollectionChart from '../tv-collection-chart'
import useComponentSize from '@/utils/hooks/useComponentSize'
import BitmapSearch from '@/components/bitmap-search'
import BitmapMarketExplorer from '@/components/bitmap/market-explorer'
import BitmapExplorerBar from '@/components/bitmap/bitmap-explorer-bar'
import event from '@/utils/event'
import useEscrows from '@/utils/hooks/useEscrows'
import {
	buildBitmapFilterAttributes,
	matchesBitmapNumberFilters,
	splitBitmapFilters,
	expandBitmapAttributeFilters,
	bitmapFilterSummary
} from '@/utils/bitmap-filters'
import { collectionObjectApiPath, isCollectionObjectSlug } from '@/utils/collection-object'

// Shadcn components
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select'
import Link from 'next/link'
import { Filter } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'

let INITIAL_RENDER = false

const inscriptionFetcher: Fetcher<Inscription[], { url: string; filter: any; method?: string }> = ({
	url,
	filter,
	method
}) => {
	if (method === 'GET') {
		return fetch(url).then((res) => res.json())
	}

	return fetch(url, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(filter)
	}).then((res) => res.json())
}

interface Attribute {
	trait_type: string
	values: { count: number; value: string }[]
}

const LIMIT = 64

const getInscriptionSortValue = (inscription: any): number => {
	const rawNum = inscription?.num
	if (typeof rawNum === 'number' && Number.isFinite(rawNum)) return rawNum

	if (typeof rawNum === 'string') {
		const parsed = Number.parseInt(rawNum, 10)
		if (Number.isFinite(parsed)) return parsed
	}

	const name = inscription?.meta?.name || inscription?.escrow?.name || inscription?.name
	const match = name?.match?.(/^(\d+)\.bitmap$/i)
	return match ? parseInt(match[1], 10) : Number.MAX_VALUE
}

const RemoteCollectionFeed: FC<{
	title: string
	items: any[]
	slug: string
	supply: number
	listed: number
	meta: Collection
	isMobile: boolean
	attributes?: Attribute[]
	onSelectBulk: (id: Inscription) => void
	bulkSelected: any
	disablePreview?: boolean
}> = (props): ReactElement => {
	const { onSelectBulk, disablePreview } = props
	const { width, ref } = useComponentSize()

	// Track selection state locally, synced with add-to-cart events
	const [localSelected, setLocalSelected] = useState<Record<string, boolean>>({})

	// Listen for add-to-cart events to sync selection state
	useEffect(() => {
		const handleAddToCart = (payload: Record<string, unknown> = {}) => {
			const id = typeof payload.id === 'string' ? payload.id : null
			if (!id) return

			setLocalSelected((prev) => {
				const newState = { ...prev }
				if (newState[id]) {
					delete newState[id]
				} else {
					newState[id] = true
				}
				return newState
			})
		}
		event.listen('add-to-cart', handleAddToCart)
		return () => {
			event.removeListener('add-to-cart', handleAddToCart)
		}
	}, [])

	// Merge with bulkSelected prop
	const bulkSelected = useMemo(() => {
		return { ...props.bulkSelected, ...localSelected }
	}, [props.bulkSelected, localSelected])

	const isVirtualObject = isCollectionObjectSlug(props.slug)

	const { data: freshAttributes } = useSWR(
		!isVirtualObject ? `${API_URL}/collection/${props.slug}/attributes` : null,
		(url: string) => fetch(url).then((r) => r.json()),
		{ fallbackData: props.attributes, refreshInterval: 600_000 }
	)

	const isNames = TEXT_COLLECTION_SLUGS.includes(props.slug)
	const isBitmap = props.slug === 'bitmap'
	const isSub100k = props.slug === 'sub-100k' || props.slug === 'sub-1k'

	// Fetch sub-collections for sub-100k/sub-1k
	const maxNum = props.slug === 'sub-1k' ? 1000 : 100000
	const { data: subCollectionsRaw } = useSWR(
		isSub100k ? `${API_URL}/collections?limit=500` : null,
		(url: string) => fetch(url).then((r) => r.json()),
		{ revalidateOnFocus: false }
	)
	const { data: subCollectionsRaw2 } = useSWR(
		isSub100k ? `${API_URL}/collections?limit=500&offset=500` : null,
		(url: string) => fetch(url).then((r) => r.json()),
		{ revalidateOnFocus: false }
	)
	const subCollections = useMemo(() => {
		if (!isSub100k) return []
		const all = [
			...(subCollectionsRaw?.collections || []),
			...(subCollectionsRaw2?.collections || [])
		]
		return all.filter(
			(c: any) =>
				c.highest_inscription_num < maxNum &&
				c.total_supply > 0 &&
				c.slug !== props.slug &&
				c.slug !== 'sub-10k' &&
				c.slug !== 'sub-1k' &&
				c.slug !== 'sub-100k'
		)
	}, [isSub100k, subCollectionsRaw, subCollectionsRaw2, maxNum, props.slug])

	const [subSort, setSubSort] = useState<'volume' | 'az' | 'supply'>('volume')
	const sortedSubCollections = useMemo(() => {
		if (!subCollections.length) return []
		return [...subCollections].sort((a: any, b: any) => {
			if (subSort === 'az') return (a.name || '').localeCompare(b.name || '')
			if (subSort === 'supply') return b.total_supply - a.total_supply
			return (b.volume_all_time || 0) - (a.volume_all_time || 0)
		})
	}, [subCollections, subSort])
	const [selected, setSelected] = React.useState('PriceAsc')
	const [_gridView, _setGridView] = React.useState(
		(!props.isMobile && !isNames) || props?.slug == 'bitmap'
	)
	const [chartView, _setChartView] = React.useState(false)
	const [selectedTab, setSelectedTab] = React.useState('listed')
	const [filters, setFilters] = React.useState<{ trait_type: string; values: string[] }[]>([])
	const [search, setSearch] = React.useState('')
	const [searchInput, setSearchInput] = React.useState('')
	const debouncedValue: string = useDebounce<string>(search, 300)

	const attributes = useMemo(() => {
		if (isBitmap) {
			return buildBitmapFilterAttributes(freshAttributes || props.attributes || [], props.supply)
		}

		if (!isNames) {
			return [...(freshAttributes || [])]
		}

		return [
			{
				trait_type: 'Characters',
				values: freshAttributes?.[0]?.values?.sort(
					(a: any, b: any) => parseInt(a?.value) - parseInt(b?.value)
				)
			},
			...(freshAttributes || [])
				.filter((e: any) => e.trait_type !== 'Letters')
				.map((e: any) => {
					if (e.trait_type === 'Range') {
						return {
							...e,
							values: e.values
								.sort((a: any, b: any) => {
									if (a.value === 'sub 100k') {
										return -1
									}

									return parseInt(a.value) - parseInt(b.value)
								})
								.map((v: any) => ({ ...v, count: Math.min(100000, v.count) }))
						}
					}

					return e
				})
		]
	}, [freshAttributes, isBitmap, isNames, props.attributes, props.supply])

	const { numberFilters, attributeFilters } = useMemo(() => splitBitmapFilters(filters), [filters])
	const requestFilters = isBitmap ? attributeFilters : filters
	const hasBitmapNumberFilters = isBitmap && numberFilters.length > 0
	const useBitmapEscrowRange =
		hasBitmapNumberFilters &&
		selectedTab === 'listed' &&
		requestFilters.length === 0 &&
		!debouncedValue
	const { data: bitmapEscrows, isLoading: isBitmapEscrowsLoading } = useEscrows(
		useBitmapEscrowRange ? props.slug : null
	)

	// Update attribute filters (moved up for use in search handlers)
	const updateFilters = React.useCallback((trait_type: string, values: string[]) => {
		INITIAL_RENDER = false

		const current = { trait_type, values }
		setFilters((prev) => {
			const index = prev.findIndex((e) => e.trait_type === trait_type)

			if (values?.length === 0) {
				if (index !== -1) {
					prev.splice(index, 1)
					return [...prev]
				}
				return prev
			} else {
				if (index !== -1) {
					prev[index] = current
					return [...prev]
				} else {
					return [...prev, current]
				}
			}
		})
	}, [])

	// Clear all filters
	const clearAllFilters = React.useCallback(() => {
		setFilters([])
	}, [])

	// Handle bitmap search - auto-detect search type and check against all attributes
	const handleBitmapSearch = useCallback(
		(value: string) => {
			INITIAL_RENDER = false
			const lowerValue = value.toLowerCase().trim()

			// Check if it matches any attribute value from any trait type (case insensitive)
			let matchedAttribute: { trait_type: string; value: string } | null = null

			for (const trait of attributes || []) {
				const matchedValue = trait.values?.find((v: any) => v.value.toLowerCase() === lowerValue)
				if (matchedValue) {
					matchedAttribute = { trait_type: trait.trait_type, value: matchedValue.value }
					break
				}
			}

			if (matchedAttribute) {
				// Apply as an attribute filter
				updateFilters(matchedAttribute.trait_type, [matchedAttribute.value])
				setSearch('') // Clear text search when using attribute filter
			} else if (/^\d+$/.test(value)) {
				// If it's a pure number, treat it as block height and append .bitmap
				setSearch(`${value}.bitmap`)
			} else {
				// Otherwise search as-is (name, partial match, etc.)
				setSearch(value)
			}
		},
		[updateFilters, attributes]
	)

	const handleClearSearch = useCallback(() => {
		setSearch('')
		setSearchInput('')
		// Clear all filters when clearing search
		clearAllFilters()
		INITIAL_RENDER = false
	}, [clearAllFilters])

	const { data, isLoading, size, setSize } = useSWRInfinite(
		(index, _prev) => {
			if (useBitmapEscrowRange || (isBitmap && selectedTab === 'listed')) return null

			const list = 'inscriptions'

			const attributes = isBitmap
				? expandBitmapAttributeFilters(requestFilters)
				: requestFilters
						?.filter((f) => f.trait_type !== 'Satributes')
						?.flatMap((f) => f.values.map((v) => `${f.trait_type}:${v}`))

			const satributes = requestFilters
				?.filter((f) => f.trait_type === 'Satributes')
				?.map((f) => f.values)
				?.flat()

			const apiPath = collectionObjectApiPath(props.slug)
			let url = `${API_URL}/${apiPath}/${list}?offset=${index * LIMIT}&order=${selected}&listed=${
				selectedTab === 'listed'
			}&limit=${LIMIT}`

			if (debouncedValue) {
				url = `${url}&search=${encodeURIComponent(debouncedValue)}`
			}

			let filter: any = {}
			if (attributes?.length > 0) {
				filter.attributes = attributes
			}

			if (satributes?.length > 0) {
				filter.satributes = satributes
			}

			return { url, filter, method: isVirtualObject ? 'GET' : 'POST' }
		},
		inscriptionFetcher,
		{ initialSize: 1, revalidateFirstPage: false, refreshInterval: 5000 }
	)

	const bitmapRangeInscriptions = useMemo(() => {
		if (!useBitmapEscrowRange || !bitmapEscrows) return null

		const rangeItems = bitmapEscrows
			.filter(
				(escrow: any) => escrow?.satoshi_price && matchesBitmapNumberFilters(escrow, numberFilters)
			)
			.map((escrow: any) => ({
				id: escrow.inscription_id,
				num: getInscriptionSortValue(escrow),
				content_type: 'text/plain;charset=utf-8',
				meta: { name: escrow.name, attributes: [] },
				collection: { name: props.meta?.name, slug: props.slug },
				escrow
			}))

		return rangeItems.sort((a: any, b: any) => {
			if (selected === 'InscriptionAsc') {
				return getInscriptionSortValue(a) - getInscriptionSortValue(b)
			}
			if (selected === 'InscriptionDesc') {
				return getInscriptionSortValue(b) - getInscriptionSortValue(a)
			}
			if (selected === 'PriceDesc') {
				return (b?.escrow?.satoshi_price || 0) - (a?.escrow?.satoshi_price || 0)
			}
			if (selected === 'ListedDesc') {
				const aTime = a?.escrow?.created ? new Date(a.escrow.created).getTime() : 0
				const bTime = b?.escrow?.created ? new Date(b.escrow.created).getTime() : 0
				return bTime - aTime
			}
			return (
				(a?.escrow?.satoshi_price || Number.MAX_VALUE) -
				(b?.escrow?.satoshi_price || Number.MAX_VALUE)
			)
		})
	}, [bitmapEscrows, numberFilters, props.meta?.name, props.slug, selected, useBitmapEscrowRange])

	// Deduplicate inscriptions by id to prevent duplicate listings
	const inscriptions: any[] = useMemo(() => {
		let rawData =
			bitmapRangeInscriptions || (data?.length ? ([] as any[]).concat(...data) : props.items)
		if (!rawData) return []

		if (!useBitmapEscrowRange && hasBitmapNumberFilters) {
			rawData = rawData.filter((item: any) => matchesBitmapNumberFilters(item, numberFilters))
		}

		const seen = new Set<string>()
		return rawData.filter((item: any) => {
			if (seen.has(item.id)) {
				return false
			}
			seen.add(item.id)
			return true
		})
	}, [
		bitmapRangeInscriptions,
		data,
		hasBitmapNumberFilters,
		numberFilters,
		props.items,
		useBitmapEscrowRange
	])

	// Range filters use the complete escrow list. Mount only a page of live
	// bitmap canvases, just like the server-paginated unfiltered grid.
	const [rangeVisibleCount, setRangeVisibleCount] = useState(32)
	useEffect(() => setRangeVisibleCount(32), [filters, selected])
	const visibleInscriptions = useBitmapEscrowRange
		? inscriptions.slice(0, rangeVisibleCount)
		: inscriptions

	const hasRank = Boolean(inscriptions?.[0]?.meta?.rank)

	const options = useMemo(() => {
		return [
			{
				title: 'Lowest price',
				id: 'PriceAsc',
				onClick: () => {
					INITIAL_RENDER = false
					setSelected('PriceAsc')
				}
			},
			hasRank && {
				title: 'Rank',
				id: 'RankAsc',
				onClick: () => {
					INITIAL_RENDER = false
					setSelected('RankAsc')
				}
			},
			{
				title: 'Highest price',
				id: 'PriceDesc',
				onClick: () => {
					INITIAL_RENDER = false
					setSelected('PriceDesc')
				}
			},
			{
				title: 'Lowest inscription',
				id: 'InscriptionAsc',
				onClick: () => {
					INITIAL_RENDER = false
					setSelected('InscriptionAsc')
				}
			},
			{
				title: 'Highest inscription',
				id: 'InscriptionDesc',
				onClick: () => {
					INITIAL_RENDER = false
					setSelected('InscriptionDesc')
				}
			},
			{
				title: 'Recently listed',
				id: 'ListedDesc',
				onClick: () => {
					INITIAL_RENDER = false
					setSelected('ListedDesc')
				}
			}
		].filter((e) => !!e)
	}, [hasRank])

	const TABS = useMemo(
		() => [
			{ value: 'listed', label: 'For Sale' },
			{ value: 'show-all', label: 'Show All' }
		],
		[]
	)

	const handleTabChange = (value: string) => {
		setSelectedTab(value)
	}

	const fetchMore = React.useCallback(async () => {
        if (useBitmapEscrowRange) setRangeVisibleCount(count => count + 32)
        else await setSize(size + 1)
    }, [setSize, size, useBitmapEscrowRange])

	const _tableColumns = [{ label: `${props.listed} Listed` }, { label: 'Buy Now' }]

	const hasAttributes = attributes?.length > 0
	const displayLoading = useBitmapEscrowRange ? isBitmapEscrowsLoading : isLoading

	if (isBitmap && selectedTab === 'listed') return <BitmapMarketExplorer onSelect={onSelectBulk} selected={bulkSelected} onShowAll={() => handleTabChange('show-all')} disablePreview={disablePreview} />

	return (
		<section className={`${styles.container} w-full overflow-hidden`} ref={ref}>
			<div className="sticky top-0 bg-background z-10 border-b border-border">
				<div className="md:py-4 py-3 px-4 flex flex-col gap-2.5 sm:gap-3">
					<div className="flex flex-wrap justify-between items-start sm:items-center gap-2 sm:gap-4">
						<div className="flex items-center gap-2"><Tabs value={selectedTab} onValueChange={handleTabChange}>
							<TabsList className="h-7 sm:h-10">
								{TABS.map((tab) => (
									<TabsTrigger
										key={tab.value}
										value={tab.value}
										className="text-xs sm:text-sm px-2 py-0.5 sm:px-3 sm:py-1.5"
									>
										{tab.label}
									</TabsTrigger>
								))}
							</TabsList>
						</Tabs>{isBitmap && <Bitmap3dToggle prominent />}</div>
						<div className="flex items-center gap-x-1.5 sm:space-x-2">
							<Select
								value={selected}
								onValueChange={(value) => {
									INITIAL_RENDER = false
									setSelected(value)
								}}
							>
								<SelectTrigger className="w-[130px] sm:w-[180px] h-7 sm:h-10 text-xs sm:text-sm">
									<SelectValue placeholder="Sort by" />
								</SelectTrigger>
								<SelectContent>
									<SelectGroup>
										<SelectLabel>Sort Options</SelectLabel>
										{options.map((option) => (
											<SelectItem key={(option as any).id} value={(option as any).id}>
												{(option as any).title}
											</SelectItem>
										))}
									</SelectGroup>
								</SelectContent>
							</Select>

							{hasAttributes && (
								<Sheet>
									<SheetTrigger asChild>
										<Button
											variant="outline"
											size="icon"
											className="size-7 sm:size-10"
											aria-label="Open filters"
										>
											<Filter className="size-3 sm:size-4" />
										</Button>
									</SheetTrigger>
									<SheetContent
										side="right"
										className="w-[85vw] max-w-[400px] sm:max-w-[540px] max-h-[100vh] overflow-y-auto"
									>
										<SheetHeader>
											<SheetTitle>Filters</SheetTitle>
										</SheetHeader>
										<div className="mt-4 pr-2">
											<TraitFilters
												traits={attributes}
												onChange={updateFilters}
												currentFilters={filters}
											/>
										</div>
									</SheetContent>
								</Sheet>
							)}

							{isSub100k && subCollections.length > 0 && (
								<Sheet>
									<SheetTrigger asChild>
										<Button
											variant="outline"
											size="icon"
											className="size-7 sm:size-10"
											aria-label="Browse collections"
										>
											<Filter className="size-3 sm:size-4" />
										</Button>
									</SheetTrigger>
									<SheetContent
										side="right"
										className="w-[85vw] max-w-[400px] sm:max-w-[540px] max-h-[100vh] overflow-y-auto"
									>
										<SheetHeader>
											<SheetTitle>Collections</SheetTitle>
										</SheetHeader>
										<Tabs
											value={subSort}
											onValueChange={(v) => setSubSort(v as any)}
											className="mt-3"
										>
											<TabsList className="h-8 w-full">
												<TabsTrigger value="volume" className="text-xs flex-1">
													Volume
												</TabsTrigger>
												<TabsTrigger value="az" className="text-xs flex-1">
													A-Z
												</TabsTrigger>
												<TabsTrigger value="supply" className="text-xs flex-1">
													Supply
												</TabsTrigger>
											</TabsList>
										</Tabs>
										<div className="mt-3 flex flex-col gap-1 pr-2">
											{sortedSubCollections.map((c: any) => (
												<Link
													key={c.slug}
													href={`/collection/${encodeURIComponent(c.slug)}`}
													className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-foreground/5 transition-colors"
												>
													<div className="flex items-center gap-2.5 min-w-0">
														{(c.icon || c.icon_inscription) && (
															<img
																src={
																	c.icon_inscription
																		? `https://cdn.ordinalswallet.com/inscription/content/${c.icon_inscription}`
																		: c.icon
																}
																alt=""
																className="size-7 rounded object-cover shrink-0"
																onError={(e) => {
																	;(e.target as HTMLImageElement).style.display = 'none'
																}}
															/>
														)}
														<span className="text-sm truncate">{c.name}</span>
													</div>
													<span className="text-xs text-muted-foreground shrink-0 ml-2">
														{subSort === 'volume'
															? `${((c.volume_all_time || 0) / 1e8).toFixed(2)} BTC`
															: c.total_supply?.toLocaleString()}
													</span>
												</Link>
											))}
										</div>
									</SheetContent>
								</Sheet>
							)}
						</div>
					</div>

					{/* Bitmap Search */}
					{isBitmap && (
						<BitmapSearch
							value={searchInput}
							onChange={setSearchInput}
							onSearch={handleBitmapSearch}
							onClear={handleClearSearch}
							className="w-full"
						/>
					)}
					{isBitmap && (
						<BitmapExplorerBar
							filters={filters}
							onChange={updateFilters}
							onClear={handleClearSearch}
							attributes={attributes}
						/>
					)}
				</div>
			</div>

			<div className="px-4 md:p-4">
				{chartView && (
					<div className="mt-8">
						<CollectionChart height={600} width={width} slug={props.slug} />
					</div>
				)}
				{/* Active search/filter indicator for bitmap */}
				{isBitmap && (search || filters.length > 0) && (
					<div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
						{filters.length > 0 ? (
							<span>
								Filtering by:{' '}
								{filters.map((f, i) => (
									<span key={(f as any).trait_type}>
										{i > 0 && ', '}
										<span className="font-semibold text-foreground">
											{bitmapFilterSummary((f as any).trait_type, (f as any).values)}
										</span>
									</span>
								))}
							</span>
						) : (
							<span>
								Searching for: <span className="font-semibold text-foreground">{search}</span>
							</span>
						)}
						<Button
							variant="ghost"
							size="sm"
							onClick={handleClearSearch}
							className="h-6 px-2 text-xs"
						>
							Clear
						</Button>
					</div>
				)}


				{!displayLoading && inscriptions?.length === 0 ? (
					<>
					<InstantSell slug={props.slug} name={props.meta.name} compact />
					<EmptyState className="mt-8" title={search ? 'No Results Found' : 'No Listings'}>
						<div className="text-center">
							{search ? (
								<>
									<p className="mb-2">No bitmaps found for &quot;{search}&quot;</p>
									<span
										onClick={handleClearSearch}
										className="font-bold cursor-pointer text-blue-500 hover:text-blue-700 dark:hover:text-blue-400"
										role="button"
										tabIndex={0}
										onKeyDown={onKeyboardClick(handleClearSearch)}
									>
										Clear search
									</span>
									{' or '}
									<span
										onClick={() => handleTabChange('show-all')}
										className="font-bold cursor-pointer text-blue-500 hover:text-blue-700 dark:hover:text-blue-400"
										role="button"
										tabIndex={0}
										onKeyDown={onKeyboardClick(() => handleTabChange('show-all'))}
									>
										Show All
									</span>
								</>
							) : (
								<>
									<span
										onClick={() => handleTabChange('show-all')}
										className="font-bold cursor-pointer"
										role="button"
										tabIndex={0}
										onKeyDown={onKeyboardClick(() => handleTabChange('show-all'))}
									>
										Show All
									</span>{' '}
									to see all inscriptions in this collection
								</>
							)}
						</div>
					</EmptyState>
					</>
				) : (
					<InfiniteScroll
						dataLength={visibleInscriptions.length}
						next={fetchMore}
						hasMore={useBitmapEscrowRange ? visibleInscriptions.length < inscriptions.length : inscriptions?.length % LIMIT === 0}
						loader={
							<div className="flex justify-center my-4">
								{!displayLoading && inscriptions?.length > 0 ? <Loading /> : null}
							</div>
						}
						scrollableTarget="collection-feed"
					>
						{displayLoading && !visibleInscriptions.length ? <div className="flex justify-center py-8" role="status" aria-label="Loading listings"><Loading /></div> : <ItemGrid
							className={styles.cardsGrid}
							disableDefaultLayout={true}
							isLoading={false}
						>
							<MarketTile slug={props.slug} name={props.meta.name} />
							<BitmapLiveProvider value={isBitmap}>
								{visibleInscriptions.map((e) => (
									<InscriptionCardWithPreview
										key={e.id}
										inscription={e}
										onClick={() => onSelectBulk(e)}
										selected={bulkSelected[e.id]}
										disablePreview={disablePreview}
										footer={
											<InscriptionActions
												inscription={e}
												owner={false}
												actionButtonColor="black"
												onlyShowPrice={true}
												collectionCard={{ slug: props.slug, name: props.meta.name }}
												hideView={true}
												onClickBuy={() => onSelectBulk(e)}
												selected={bulkSelected[e.id]}
											/>
										}
									/>
								))}
							</BitmapLiveProvider>
						</ItemGrid>}
					</InfiniteScroll>
				)}
			</div>
		</section>
	)
}

export default RemoteCollectionFeed
